import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperCore } from 'swiper/types';
import {
  A11y,
  FreeMode,
  Keyboard,
  Mousewheel,
  Navigation,
  Pagination,
  Scrollbar,
} from 'swiper/modules';

import { CarouselProps } from '@msc-b2c/interfaces/types';
import { Button } from '../button/Button';
import {
  breakpoints as responsivePoints,
  useWindowSize,
  getEditorialCarouselNextIconLabel,
  getEditorialCarouselPrevIconLabel,
  getOverwriteExistingLabelsForCarouselLabels,
  setReactAccAttributes,
  wireCarouselCloneClickDelegation,
} from '@msc-b2c/shared-utils';
import { Icon } from '../icons/Icon';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import 'swiper/css/mousewheel';

import './_carousel.scss';

export function Carousel({
  id,
  data,
  swiperOptions = {},
  showIndicator = { mobile: true, tablet: true, desktop: true },
  showInNavIndicator = { mobile: false, tablet: false, desktop: false },
  showArrows = false,
  leftArrowIcon = 'left',
  rightArrowIcon = 'right',
  isFreeMode = false,
  isProgressBar = false,
  isOffsetNeeded = false,
  customOffsetValue,
  buttonLink = { url: undefined, valid: false },
  buttonClick,
  buttonLinkLabel = '',
  buttonLinkAriaLabel = '',
  buttonLinkIcon = '',
  styleOption = '',
  renderItem,
  handleActiveSlide,
  previousIconAriaLabel,
  nextIconAriaLabel,
  enableItineraryCardLayout,
}: Readonly<CarouselProps>) {
  const [isSwiperLocked, setIsSwiperLocked] = useState<boolean>(false);

  // Get the window width using the custom hook in order to show/hide the indicator
  const { width } = useWindowSize();
  const isMobile = width < responsivePoints.md;
  const isTablet = width >= responsivePoints.md && width < responsivePoints.lg;
  const isDesktop = width >= responsivePoints.lg;

  let offsetValue = 0;
  if (isOffsetNeeded) {
    offsetValue =
      customOffsetValue ??
      (width < responsivePoints.s
        ? 8
        : width < responsivePoints.md
        ? 16
        : width < responsivePoints.lg
        ? 24
        : width < responsivePoints.xl
        ? 32
        : width < responsivePoints.xxl
        ? 88
        : 0);
  }

  let isIndicatorVisible = false;
  if (
    (isMobile && showIndicator.mobile) ||
    (isTablet && showIndicator.tablet) ||
    (isDesktop && showIndicator.desktop)
  ) {
    isIndicatorVisible = true;
  }

  let isInNavIndicatorVisible = false;
  if (
    (isMobile && showInNavIndicator.mobile) ||
    (isTablet && showInNavIndicator.tablet) ||
    (isDesktop && showInNavIndicator.desktop)
  ) {
    isInNavIndicatorVisible = true;
  }

  // if we have no swiper indicators we will apply a bottom 0
  // on button so not to ovelap card else we have the normal css flow
  function checkButtonPosition(swiperIsLocked: boolean, carouselId: string) {
    const swiperItem = document.getElementById(carouselId) as HTMLElement;
    if (!swiperItem) return;
    const cardCarousel = swiperItem.closest('.itinerarycruisecard.cmp-vertical-carousel');
    if (cardCarousel) {
      const buttonLink = cardCarousel.nextElementSibling;
      if (!swiperIsLocked) {
        buttonLink?.classList.remove('carousel-without-actions');
      } else {
        buttonLink?.classList.add('carousel-without-actions');
      }
    }
  }

  const swiperRef = useRef<SwiperCore | null>(null); // Ref for Swiper
  const tabObserverCleanupRef = useRef<(() => void) | null>(null);

  const isTabPanelActive = useCallback((swiper: SwiperCore) => {
    const parentTab = swiper.el.closest('.cmp-tabs__tabpanel');
    return !parentTab || parentTab.classList.contains('cmp-tabs__tabpanel--active');
  }, []);

  const syncLockState = useCallback(
    (swiper: SwiperCore) => {
      // Ignore lock values computed while the tab content is hidden.
      if (!isTabPanelActive(swiper)) return;

      setIsSwiperLocked(swiper.isLocked);
      checkButtonPosition(swiper.isLocked, `${id}-carousel`);
    },
    [id, isTabPanelActive]
  );

  const observeTabActivation = useCallback(
    (swiper: SwiperCore) => {
      tabObserverCleanupRef.current?.();

      const parentTab = swiper.el.closest('.cmp-tabs__tabpanel');
      if (!parentTab) {
        tabObserverCleanupRef.current = null;
        return;
      }

      const observer = new MutationObserver(() => {
        if (!parentTab.classList.contains('cmp-tabs__tabpanel--active')) return;

        requestAnimationFrame(() => {
          swiper.update();
          syncLockState(swiper);
        });
      });

      observer.observe(parentTab, { attributes: true, attributeFilter: ['class'] });
      tabObserverCleanupRef.current = () => observer.disconnect();
    },
    [syncLockState]
  );

  /**
   * prevent the page from shifting horizontally when keyboard focus moves
   * between interactive elements in different itinerary carousel slides.
   * native focus scrolling changes document.body.scrollLeft
   * additional body scroll exposes empty
   *(avoid these with the code below)
   */
  const carouselCleanupRef = useRef<(() => void) | null>(null);

  const handleSwiperReady = useCallback((swiper: SwiperCore) => {
    swiperRef.current = swiper;

    //remove listeners registered for a previous Swiper instance.
    carouselCleanupRef.current?.();

    const swiperElement = swiper.el;

    //only for itinerary carousels with cruise cards.
    if (
      !swiperElement ||
      !swiperElement.closest('.itinerarycruisecard') ||
      !swiperElement.querySelector('.cruiseCard')
    ) {
      carouselCleanupRef.current = null;
      return;
    }

    let correctionFrame = 0;
    let correctionTimeout = 0;
    let correcting = false;

    //reset only the body's horizontal scroll position.
    const restoreBodyPosition = () => {
      if (correcting || document.body.scrollLeft === 0) {
        return;
      }

      correcting = true;
      document.body.scrollLeft = 0;
      correcting = false;
    };

    /**
     * repeat the correction because native focus scrolling may occur after
     * focusin, during a later rendering frame, or shortly after it on mobile.
     */
    const scheduleCorrections = () => {
      restoreBodyPosition();

      cancelAnimationFrame(correctionFrame);
      window.clearTimeout(correctionTimeout);

      correctionFrame = requestAnimationFrame(() => {
        restoreBodyPosition();

        correctionFrame = requestAnimationFrame(() => {
          restoreBodyPosition();
        });
      });

      correctionTimeout = window.setTimeout(() => {
        restoreBodyPosition();
      }, 50);
    };

    //correct the page position whenever focus enters an itinerary slide.
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;

      if (!target?.closest('.swiper-slide')) {
        return;
      }

      scheduleCorrections();
    };

    ////Catch the delayed scroll with correct focus
    const handleBodyScroll = () => {
      const activeElement = document.activeElement as HTMLElement | null;

      if (!activeElement || !swiperElement.contains(activeElement)) {
        return;
      }

      restoreBodyPosition();
    };

    swiperElement.addEventListener('focusin', handleFocusIn, true);

    document.body.addEventListener('scroll', handleBodyScroll, {
      passive: true,
    });

    carouselCleanupRef.current = () => {
      swiperElement.removeEventListener('focusin', handleFocusIn, true);
      document.body.removeEventListener('scroll', handleBodyScroll);
      cancelAnimationFrame(correctionFrame);
      window.clearTimeout(correctionTimeout);
    };
  }, []);

  const slidesWithKeys = useMemo(
    () => data.map((item) => ({ ...item, key: crypto.randomUUID() })),
    [data]
  );

  useEffect(() => {
    return () => {
      carouselCleanupRef.current?.();
      carouselCleanupRef.current = null;
      tabObserverCleanupRef.current?.();
      tabObserverCleanupRef.current = null;
    };
  }, []);

  return (
    <Swiper
      onSwiper={handleSwiperReady}
      id={`${id}-carousel`}
      className={`cmp-swiper-carousel ${isProgressBar ? '-progressbar' : ''} ${styleOption}`}
      onInit={(swiper) => {
        wireCarouselCloneClickDelegation(swiper.el);
        observeTabActivation(swiper);
        syncLockState(swiper);
      }}
      onSlidesUpdated={(swiper) => {
        syncLockState(swiper);
      }}
      onSlideChange={(swiper) => {
        if (handleActiveSlide) handleActiveSlide(data[swiper.realIndex]);
      }}
      modules={[Navigation, Scrollbar, Keyboard, Pagination, FreeMode, Mousewheel, A11y]}
      grabCursor={true}
      navigation={{
        nextEl: `.${id}-swiper-button-next`,
        prevEl: `.${id}-swiper-button-prev`,
        addIcons: false, // usiamo Icon custom, non le frecce SVG di default Swiper 12
      }}
      a11y={{
        enabled: true,
        prevSlideMessage: setReactAccAttributes(
          getEditorialCarouselPrevIconLabel(),
          previousIconAriaLabel ?? '',
          getOverwriteExistingLabelsForCarouselLabels()
        ),
        nextSlideMessage: setReactAccAttributes(
          getEditorialCarouselNextIconLabel(),
          nextIconAriaLabel ?? '',
          getOverwriteExistingLabelsForCarouselLabels()
        ),
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
        slideLabelMessage: '{{index}} / {{slidesLength}}',
      }}
      scrollbar={{
        el: `.${id}-swiper-scrollbar`,
        draggable: true,
        snapOnRelease: false,
        dragSize: 24,
      }}
      pagination={{
        el: `.${id}-swiper-pagination`,
        type: 'bullets',
        clickable: true,
      }}
      freeMode={{
        enabled: isFreeMode,
        sticky: false,
        momentumBounce: false,
        minimumVelocity: 0.01,
      }}
      mousewheel={{
        enabled: isFreeMode,
        forceToAxis: true,
      }}
      loopPreventsSliding={!isFreeMode}
      initialSlide={swiperOptions?.initialSlide ?? 0}
      slidesPerView={swiperOptions?.slidesPerView ?? 1}
      breakpointsBase={swiperOptions?.breakpointsBase ?? 'window'}
      breakpoints={swiperOptions?.breakpoints ?? {}}
      spaceBetween={swiperOptions?.spaceBetween ?? 10}
      speed={swiperOptions?.speed ?? 600}
      loop={!!swiperOptions?.loop && !isProgressBar}
      preventInteractionOnTransition={!isFreeMode}
      slidesOffsetBefore={offsetValue}
      slidesOffsetAfter={offsetValue}
    >
      {slidesWithKeys.map((carouselItem, index) => (
        <SwiperSlide key={carouselItem.key}>{renderItem(carouselItem, index)}</SwiperSlide>
      ))}

      {(showArrows || isProgressBar || isInNavIndicatorVisible) && (
        <div
          className={`swiper-actions ${isSwiperLocked ? 'hide-actions' : ''} ${
            enableItineraryCardLayout ? 'override' : ''
          }`}
        >
          {buttonLink?.valid && buttonLinkLabel ? (
            <Button
              text={buttonLinkLabel}
              icon={buttonLinkIcon}
              buttonLink={buttonLink}
              accessibilityLabel={buttonLinkAriaLabel}
              styleOption={'cmp-button__link -position-right'}
            />
          ) : buttonClick && buttonLinkLabel ? (
            <Button
              text={buttonLinkLabel}
              icon={buttonLinkIcon}
              onClick={buttonClick}
              styleOption={'cmp-button__link -position-right'}
            />
          ) : null}
          <div
            className={`navigators ${isSwiperLocked ? 'hide' : ''} ${
              enableItineraryCardLayout ? 'navigators-override' : ''
            }`}
          >
            {showArrows && (
              <div className={`swiper-button ${id}-swiper-button-prev  swiper-button-prev`}>
                <Icon
                  tooltipTitle={setReactAccAttributes(
                    getEditorialCarouselPrevIconLabel(),
                    previousIconAriaLabel ?? '',
                    getOverwriteExistingLabelsForCarouselLabels()
                  )}
                  aria-label="Left Icon"
                  icon={leftArrowIcon}
                />
              </div>
            )}
            {isProgressBar && isInNavIndicatorVisible && (
              <div className={'swiper-progress-wrapper'}>
                <div className={`${id}-swiper-scrollbar swiper-scrollbar`}></div>
              </div>
            )}
            {!isProgressBar && isInNavIndicatorVisible && (
              <div className={'swiper-pagination-wrapper'}>
                <div className={`${id}-swiper-pagination swiper-pagination`}></div>
              </div>
            )}
            {showArrows && (
              <div className={`swiper-button ${id}-swiper-button-next  swiper-button-next`}>
                <Icon
                  tooltipTitle={setReactAccAttributes(
                    getEditorialCarouselNextIconLabel(),
                    nextIconAriaLabel ?? '',
                    getOverwriteExistingLabelsForCarouselLabels()
                  )}
                  aria-label="Right Icon"
                  icon={rightArrowIcon}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {isIndicatorVisible && (
        <div className={`swiper-pagination-wrapper ${isSwiperLocked ? 'hide' : ''}`}>
          <div className={`${id}-swiper-pagination swiper-pagination`}></div>
        </div>
      )}
    </Swiper>
  );
}
